import { ListRecipientsRequest } from '@weave/schema-gen-ts/dist/schemas/messaging/etl/bulk/v1/service.pb';
import {
  GetLocationRegistrationStatusDetailsRequest,
  GetLocationRegistrationStatusDetailsResponse,
} from '@weave/schema-gen-ts/dist/schemas/tendlc/coordinator/v1/coordinator_service.pb';
import { PhoneRegistrationStatusDetails } from '@weave/schema-gen-ts/dist/schemas/tendlc/shared/v1/shared.pb';
import { useInfiniteQuery, useQuery, UseQueryOptions } from 'react-query';
import { ContextlessQueryObserverOptions } from '@frontend/react-query-helpers';
import { SchemaBulkMetl, SchemaCommPhoneNumberService, SchemaIO, SchemaTenDLCCoordinator } from '@frontend/schema';
import { useBulkQueryKeys } from './hooks';
import { validateFilters } from './utils';
import { MessagesTypes } from '.';

// TODO: Fix all error handling. When the server throws a 400 error, things just keep going like there's no problems at all!

const defaultOptions: ContextlessQueryObserverOptions = {
  refetchOnMount: true,
  refetchOnWindowFocus: false,
};

const createListRecipientsRequest: (filters: MessagesTypes.Filters) => ListRecipientsRequest = (filters) => ({
  primaryInsurance: filters.primaryInsurance,
  appointmentTypes: filters.appointmentTypes,
  appointmentStatuses: filters.appointmentStatuses,
  appointmentBeginDate: filters.appointmentDate?.[0],
  appointmentEndDate: filters.appointmentDate?.[1],
  minDaysSinceLastBulkMessage: filters?.minDaysSinceLastBulkMessage
    ? MessagesTypes.MinDaysSinceLastBulkMessageMap[filters.minDaysSinceLastBulkMessage]
    : undefined,
  minAge: filters.minAge ? Number(filters.minAge) : undefined,
  maxAge: filters.maxAge ? Number(filters.maxAge) : undefined,
  sourceTenantIds: filters.sourceTenantIds,
  recipientStatuses: filters.recipientStatuses,
  appointmentPractitioners: filters.appointmentPractitioners,
  recallTypes: filters.recallTypes,
  recallBeginDate: filters.recallDate?.[0],
  recallEndDate: filters.recallDate?.[1],
  appointmentQualifier: filters.appointmentQualifier
    ? MessagesTypes.AppointmentQualifierMap[filters.appointmentQualifier]
    : undefined,
});

export const useListRecipients = (filters: MessagesTypes.Filters, pageSize: number, searchToken?: string) => {
  const request = createListRecipientsRequest(filters);
  const queryKeys = useBulkQueryKeys();
  const sanitizedSearchToken = searchToken?.replace(/[\t\r\n]+/g, ' ').trim();

  return useInfiniteQuery({
    queryKey: queryKeys.recipients(filters, sanitizedSearchToken),
    queryFn: (queryFunctionContext) =>
      SchemaBulkMetl.ListRecipients(
        {
          ...request,
          pageSize,
          lastPersonSortName: queryFunctionContext.pageParam?.lastPersonSortName,
          lastRecipientId: queryFunctionContext.pageParam?.lastRecipientId,
          searchToken: sanitizedSearchToken,
        },
        { signal: queryFunctionContext.signal }
      ),
    getNextPageParam: (lastPage) => {
      const recipientCount = lastPage.recipients?.length ?? 0;
      if (recipientCount < pageSize) return undefined;

      const lastRecipient = lastPage.recipients?.[lastPage.recipients.length - 1];
      if (!lastRecipient) return undefined;

      const nextPageParam = {
        lastPersonSortName: lastRecipient?.personSortName,
        lastRecipientId: lastRecipient?.recipientId,
      };
      return nextPageParam;
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({ rows: page.recipients })), // formatting it for the pagination list component
    }),
  });
};

type SchemaCountRecipients = SchemaIO<(typeof SchemaBulkMetl)['CountRecipients']>;
export const useCountRecipients = (
  filters: MessagesTypes.Filters = {},
  options: UseQueryOptions<SchemaCountRecipients['output']> = {}
) => {
  const request = createListRecipientsRequest(filters);
  const queryKeys = useBulkQueryKeys();

  return useQuery({
    ...defaultOptions,
    queryKey: queryKeys.countRecipients(filters),
    queryFn: validateFilters(filters)
      ? (queryFunctionContext) => SchemaBulkMetl.CountRecipients(request, { signal: queryFunctionContext.signal })
      : async () => ({ count: undefined }),
    ...options,
  });
};

export const useRecipientFilterOptions = () => {
  const queryKeys = useBulkQueryKeys();

  return useQuery({
    ...defaultOptions,
    queryKey: queryKeys.filterOptions(),
    queryFn: () => SchemaBulkMetl.RecipientFilterOptions({}),
    select: (data) =>
      data.options?.map((option) => {
        if (!option.filterOptions) {
          return option;
        }

        const filterOptions = option.filterOptions;
        const sortedFilterOptions = Object.keys(filterOptions).sort(
          (a, b) => filterOptions[a]?.localeCompare(filterOptions[b] ?? '') ?? 0
        );
        const newFilterOptions: Record<string, string> = {};
        for (const key of sortedFilterOptions) {
          const newVal = filterOptions[key];
          if (newVal) newFilterOptions[key] = newVal;
        }
        return {
          ...option,
          filterOptions: newFilterOptions,
        };
      }),
  });
};

type SchemaTenDLCLocationRegistrationStatus = SchemaIO<
  (typeof SchemaTenDLCCoordinator)['GetLocationRegistrationStatus']
>;
export const useTenDLCLocationRegistrationStatus = ({
  locationId,
  enabled,
}: {
  locationId: string;
  enabled: boolean;
}) => {
  const request: SchemaTenDLCLocationRegistrationStatus['input'] = {
    locationId,
  };
  const queryKeys = useBulkQueryKeys();

  return useQuery({
    ...defaultOptions,
    refetchOnMount: false,
    queryKey: queryKeys.registrationStatus(),
    queryFn: () => SchemaTenDLCCoordinator.GetLocationRegistrationStatus(request),
    enabled,
  });
};

export const useLocationRegistrationStatusDetails = (
  request: GetLocationRegistrationStatusDetailsRequest,
  options: UseQueryOptions<GetLocationRegistrationStatusDetailsResponse, unknown, PhoneRegistrationStatusDetails> = {}
) =>
  useQuery({
    ...defaultOptions,
    queryKey: useBulkQueryKeys().registrationStatusDetails(),
    queryFn: () => SchemaTenDLCCoordinator.GetLocationRegistrationStatusDetails(request),
    refetchOnMount: false,
    select: (data) => data.defaultSmsStatusDetails,
    ...options,
  });

type SchemaLocationTCRBrand = SchemaIO<(typeof SchemaTenDLCCoordinator)['GetLocationTCRBrand']>;
export const useLocationTCRBrand = (locationId: string) => {
  const request: SchemaLocationTCRBrand['input'] = {
    locationId,
  };
  const queryKeys = useBulkQueryKeys();

  return useQuery({
    ...defaultOptions,
    refetchOnMount: false,
    retry: false,
    queryKey: queryKeys.locationTCRBrand(),
    queryFn: () => SchemaTenDLCCoordinator.GetLocationTCRBrand(request),
    enabled: !!locationId,
    select: (data) => data?.tcrBrand,
  });
};

type SchemaDefaultPhoneRecord = SchemaIO<(typeof SchemaCommPhoneNumberService)['GetDefaultPhoneRecord']>;
export const useDefaultPhoneRecord = (request: SchemaDefaultPhoneRecord['input']) =>
  useQuery({
    queryKey: useBulkQueryKeys().defaultPhoneRecord(),
    queryFn: () => SchemaCommPhoneNumberService.GetDefaultPhoneRecord(request),
    select: (data) => data?.phoneNumber,
  });
